import { AgentSession } from '@superfunnel/types';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAgentChat } from '../contexts/AgentChatContext';
import { UserContext } from '../contexts/UserContext';
import { db } from '../Firebase';
import { AgentChange } from '../types/agentChanges';

export interface BaseAgentSessionOptions<T extends AgentSession> {
  /**
   * Whether to enable the session listener
   * Used to conditionally disable the listener when context requirements aren't met
   */
  enabled?: boolean;

  /**
   * Custom function to generate titles for changes
   * Each agent type can provide its own title generation logic
   */
  getTitleForChange?: (change: AgentChange, sessionData: T) => string;

  /**
   * Custom function to sort changes
   * Each agent type can provide its own sorting logic
   */
  sortChanges?: (changes: AgentChange[], sessionData: T) => AgentChange[];

  /**
   * Callback when session data changes
   * Used for side effects like controlling editable state
   */
  onSessionChange?: (
    sessionData: T | null,
    hasUnreviewedChanges: boolean,
  ) => void;
}

export interface BaseAgentSessionReturn<T extends AgentSession> {
  /** List of changes with titles applied */
  agentChanges: AgentChange[];
  /** Whether there are any pending changes */
  agentHasUnreviewedChanges: boolean;
  /** Update change statuses in Firestore */
  agentUpdateChanges: (
    changeUpdates: {
      id: string;
      status: 'accepted' | 'rejected';
    }[],
  ) => Promise<void>;
  /** The full session data from Firestore */
  agentSessionData: T | null;
  /** Current session status */
  sessionStatus: T['status'] | null;
  /** The Firestore document path for the session */
  sessionDocPath: string | null;
}

/**
 * Base hook for listening to agent sessions in Firestore
 * Provides common functionality for all agent types:
 * - Firestore real-time listener
 * - Thread ID integration from AgentChatContext
 * - Change status updates
 * - Session status tracking
 *
 * @template T - The specific AgentSession type (LandingPageAgentSession | AssistantAgentSession)
 * @param options - Configuration options for the hook
 * @returns Session data, changes, and update functions
 */
export function useBaseAgentSession<T extends AgentSession>(
  options: BaseAgentSessionOptions<T> = {},
): BaseAgentSessionReturn<T> {
  const {
    enabled = true,
    getTitleForChange,
    sortChanges,
    onSessionChange,
  } = options;

  const { selectedAccount, selectedAccountMember } = useContext(UserContext);
  const { getThreadId } = useAgentChat();
  const [sessionData, setSessionData] = useState<T | null>(null);
  const [_changes, setChanges] = useState<AgentChange[]>([]);

  // Get threadId from AgentChatContext
  const threadId = getThreadId();

  // Build session document path
  const sessionDocPath = useMemo(() => {
    if (!selectedAccountMember?.userId || !selectedAccount || !threadId) {
      return null;
    }
    return `accounts/${selectedAccount.id}/users/${selectedAccountMember.userId}/agentSessions/${threadId}`;
  }, [selectedAccountMember?.userId, selectedAccount, threadId]);

  // Process changes with titles and sorting
  const changes = useMemo(() => {
    if (!sessionData) return _changes;

    // Apply custom sorting if provided
    const sortedChanges = sortChanges
      ? sortChanges(_changes, sessionData)
      : _changes;

    // Apply titles if a title generator is provided
    if (getTitleForChange) {
      return sortedChanges.map((change) => ({
        ...change,
        title: getTitleForChange(change, sessionData),
      }));
    }

    return sortedChanges;
  }, [_changes, sessionData, getTitleForChange, sortChanges]);

  // Calculate if there are unreviewed changes
  const hasUnreviewedChanges = useMemo(() => {
    return sessionData?.changes
      ? Object.values(sessionData.changes).some((c) => c.status === 'pending')
      : false;
  }, [sessionData]);

  // Listen to session document in Firestore
  useEffect(() => {
    if (!enabled || !sessionDocPath) {
      setSessionData(null);
      setChanges([]);
      return;
    }

    const sessionDocRef = doc(db, sessionDocPath);

    console.log('useBaseAgentSession - Listening to path:', sessionDocPath);

    const unsubscribe = onSnapshot(
      sessionDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as T;
          setSessionData(data);

          // Convert changes record to array
          const genericChanges: AgentChange[] = [];
          for (const [changeId, change] of Object.entries(data.changes || {})) {
            genericChanges.push({
              id: changeId,
              ...change,
              title: '', // Title will be set in the changes memo
            });
          }

          setChanges(genericChanges);
        } else {
          setSessionData(null);
          setChanges([]);
        }
      },
      (error) => {
        console.error(
          'useBaseAgentSession - Error listening to session:',
          error,
        );
        setSessionData(null);
        setChanges([]);
      },
    );

    return unsubscribe;
  }, [enabled, sessionDocPath]);

  // Call onSessionChange callback when session or unreviewed status changes
  useEffect(() => {
    onSessionChange?.(sessionData, hasUnreviewedChanges);
  }, [sessionData, hasUnreviewedChanges, onSessionChange]);

  // Function to update change statuses
  const updateChanges = useCallback(
    async (
      changeUpdates: {
        id: string;
        status: 'accepted' | 'rejected';
      }[],
    ): Promise<void> => {
      if (!sessionDocPath || !sessionData) {
        return;
      }

      const sessionDocRef = doc(db, sessionDocPath);

      const update: Record<string, unknown> = { changes: {} };
      const mergeFields: string[] = [];

      changeUpdates.forEach(({ id, status }) => {
        if (sessionData.changes[id]) {
          (update.changes as Record<string, unknown>)[id] = {
            status: status === 'rejected' ? 'discarded' : status,
          };
          mergeFields.push(`changes.${id}.status`);
        }
      });

      if (mergeFields.length > 0) {
        await setDoc(sessionDocRef, update, { mergeFields });
      }
    },
    [sessionDocPath, sessionData],
  );

  return {
    agentChanges: changes,
    agentHasUnreviewedChanges: hasUnreviewedChanges,
    agentUpdateChanges: updateChanges,
    agentSessionData: sessionData,
    sessionStatus: sessionData?.status ?? null,
    sessionDocPath,
  };
}
